import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Briefcase, ShieldCheck, Scale, PieChart, PenLine,
  TrendingUp, Clock, Activity, MessageSquare, FileText, Settings, X,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { cn } from '../utils/cn'

// Grouped IA — the flat 12-item list is now organized into intent-based sections.
const NAV_GROUPS = [
  { label: 'Overview', items: [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Portfolio', href: '/portfolio', icon: Briefcase },
    { name: 'Health',    href: '/health',    icon: ShieldCheck },
  ]},
  { label: 'Strategy', items: [
    { name: 'Rebalance',      href: '/rebalance',      icon: Scale },
    { name: 'Mutual Funds',   href: '/mutual-funds',   icon: PieChart },
    { name: 'Notes', href: '/recommendation', icon: PenLine },
  ]},
  { label: 'Markets', items: [
    { name: 'Signals',   href: '/signals',   icon: TrendingUp },
    { name: 'Intraday',  href: '/intraday',  icon: Clock },
    { name: 'Streaming', href: '/streaming', icon: Activity },
  ]},
  { label: 'Workspace', items: [
    { name: 'Prompt',   href: '/prompt',   icon: MessageSquare },
    { name: 'Reports',  href: '/reports',  icon: FileText },
    { name: 'Settings', href: '/settings', icon: Settings },
  ]},
]

function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-accent-500 to-accent-700 shadow-glow-accent flex items-center justify-center">
        <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
      </div>
      <div className="leading-none">
        <div className="font-display text-lg font-bold tracking-tighter text-ink-900">
          seren<span className="text-accent-600">DPT</span>
        </div>
        <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-ink-400 mt-0.5">Trading Desk</div>
      </div>
    </div>
  )
}

function NavItem({ item, active, onClick }) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.href}
      onClick={onClick}
      className={cn(
        'group relative flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200 ease-out-quint',
        active
          ? 'bg-white text-ink-900 shadow-elev-1'
          : 'text-ink-500 hover:text-ink-900 hover:bg-white/70',
      )}
    >
      {active && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-accent-500" />
      )}
      <Icon
        className={cn('w-[18px] h-[18px] shrink-0 transition-colors',
          active ? 'text-accent-600' : 'text-ink-400 group-hover:text-ink-600')}
        strokeWidth={active ? 2.4 : 2}
      />
      <span className="truncate">{item.name}</span>
    </NavLink>
  )
}

function SidebarContent({ onNavigate }) {
  const { user } = useAuth()
  const location = useLocation()
  return (
    <div className="flex min-h-0 flex-1 flex-col bg-ink-25 border-r border-ink-100">
      {/* Brand */}
      <div className="flex h-16 shrink-0 items-center px-5 border-b border-ink-100">
        <Brand />
      </div>

      {/* Grouped navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-5 no-scrollbar">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-300">
              {group.label}
            </div>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavItem
                  key={item.href}
                  item={item}
                  active={location.pathname === item.href}
                  onClick={onNavigate}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Account footer */}
      <div className="shrink-0 p-3 border-t border-ink-100">
        <div className="flex items-center gap-3 rounded-xl px-2.5 py-2.5 hover:bg-white/70 transition-colors">
          <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-ink-700 to-ink-900 flex items-center justify-center text-sm font-semibold text-white shrink-0">
            {user?.email?.charAt(0).toUpperCase() || 'U'}
            <span className={cn(
              'absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-ink-25',
              user?.kiteConnected ? 'bg-gain-500' : 'bg-ink-300')}
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ink-800 truncate">{user?.email || 'Account'}</p>
            <p className="text-xs text-ink-400 truncate">
              {user?.kiteConnected ? 'Kite · connected' : 'Kite · offline'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

const Sidebar = ({ open, setOpen }) => {
  return (
    <>
      {/* Desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <SidebarContent />
      </div>

      {/* Mobile drawer */}
      <div
        className={cn(
          'fixed inset-0 z-50 lg:hidden transition-transform duration-300 ease-out-quint',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="relative flex min-h-0 h-full w-64 flex-col">
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-3 z-10 p-2 rounded-lg text-ink-400 hover:text-ink-700 hover:bg-ink-100"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
          <SidebarContent onNavigate={() => setOpen(false)} />
        </div>
      </div>
    </>
  )
}

export default Sidebar
